import { type RefObject, useEffect, useLayoutEffect, useRef } from 'react';

/**
 * Listen for scroll events on an element in a performant way.
 * Option to listen for scroll end events if supported.
 *
 * @example
 * const Example = () => {
 *  const ref = useRef(null);
 *   useScrollListener((event) => {
 *    console.log('scroll', event);
 *  }, ref);
 *
 *  return <div ref={ref} />;
 * }
 */
export const useScrollListener = (
    listener: (event: Event) => void,
    elementRef: RefObject<Element | Window | null> | null,
    options?: {
        capture?: boolean;
        useScrollEnd?: boolean;
    }
) => {
    const listenerRef = useRef(listener);
    useLayoutEffect(() => {
        listenerRef.current = listener;
    });
    useEffect(() => {
        const element = elementRef?.current;
        if (!element) {
            return undefined;
        }
        let ticking = false;
        const listener = (ev: Event) => {
            if (ev.type === 'scrollend') {
                listenerRef.current(ev);
                return;
            }
            if (ticking) {
                return;
            }
            requestAnimationFrame(() => {
                listenerRef.current(ev);
                ticking = false;
            });
            ticking = true;
        };
        const useScrollEnd = (options?.useScrollEnd && 'onscrollend' in element) ?? false;
        element.addEventListener(useScrollEnd ? 'scrollend' : 'scroll', listener, {
            passive: true,
            capture: options?.capture,
        });
        return () => {
            element.removeEventListener(useScrollEnd ? 'scrollend' : 'scroll', listener, {
                capture: options?.capture,
            });
        };
    }, [elementRef, options?.capture, options?.useScrollEnd]);
};
